import { Inject, Injectable } from '@angular/core';

import { NEVER, Observable, of, throwError, timer } from 'rxjs';
import { map, switchMap, takeUntil, tap, filter } from 'rxjs/operators';

import { getUniqueOpid, filterByOpid, filterByChannel } from './utils';
import {
  ConnectionStatus,
  CoreWebsocketService,
  Message,
  MessageRequest,
  MessageResponse,
  MessageStatus,
  REQUEST_ERROR_HANDLER,
  RequestErrorHandler,
  TOKEN_SERVICE,
  TokenService,
  WebsocketsOptions,
} from './core';

@Injectable()
export class WebsocketDispatcherService {
  private _channels: Map<string, any> = new Map();

  constructor(
    private socket: CoreWebsocketService<MessageRequest | MessageResponse>,
    @Inject(TOKEN_SERVICE) private tokenService: TokenService,
    @Inject(REQUEST_ERROR_HANDLER) private requestErrorHandler: RequestErrorHandler,
    @Inject(WebsocketsOptions) private options: WebsocketsOptions,
  ) {
    this.socket.connectionStatus
      .asObservable()
      .pipe(
        filter((status) => status === ConnectionStatus.OpenedAfterRetry),
        tap(() => this.resubscribeChannels()),
      )
      .subscribe();
  }

  request<R>(url: string, msg?: any, unsubscribeMsg: (() => void) | false = () => {}, timeout = 30000): Observable<R> {
    const data = new Message<any>(url, this.tokenService.getToken() as string, msg, getUniqueOpid());
    const message = new MessageRequest(data);
    const timer$ = timeout ? timer(timeout) : NEVER;

    return this.socket.request<MessageResponse<R>>(message, filterByOpid(), unsubscribeMsg).pipe(
      takeUntil(timer$),
      map((value) => value.response),
      switchMap((value) => {
        if (value.status === MessageStatus.Success) {
          return of(value.data);
        } else if (value.status === MessageStatus.Error) {
          return this.requestErrorHandler.handle(value);
        }

        return throwError(() => new Error(`detail: incorrect status`));
      }),
    );
  }

  bindToChannel<R = any>(channel: string, eventType: string, msg: any): Observable<R> {
    const message = {
      type: eventType,
      channel: channel,
      token: this.tokenService.getToken(),
      ...msg,
    };

    const unsubMsg = { ...message, type: 'unsub', subscription_type: eventType };

    this._channels.set(`${channel}/${eventType}`, { channel, eventType, msg });

    return this.socket
      .request<{ channel: string; data: R }>(new MessageRequest(message), filterByChannel(), () => {
        this._channels.delete(`${channel}/${eventType}`);

        return new MessageRequest(unsubMsg);
      })
      .pipe(map((o) => o.data));
  }

  resubscribeChannels() {
    if (this.options.debug) {
      console.log(`Subscribed channels: `, this._channels);
    }

    for (const channelData of this._channels.values()) {
      const message = {
        type: channelData.eventType,
        channel: channelData.channel,
        token: this.tokenService.getToken(),
        ...channelData.msg,
      };

      this.socket.getWebSocketSubject().next(new MessageRequest(message));
    }
  }
}
